import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
	Box,
	FormControlLabel,
	RadioGroup,
	Stack,
	Typography
} from '@mui/material';
import { useMedia } from '@/hooks/responsive';
import { Radio } from '../Radio';
import { Button } from '../Button';

const MAX_ITEMS_COLLAPSED = 4;

const aligns = {
	left: 'flex-start',
	center: 'center',
	right: 'flex-end'
};

export function RadioGroupList({
	ariaLabelledby,
	options: optionsProp = [],
	name,
	maxItemsCollapsed = MAX_ITEMS_COLLAPSED,
	seeMoreAlign = 'center',
	showSeeMore = true,
	value,
	sx,
	showMobileDivider,
	blockedOptions = [],
	...props
}) {
	const [collapsed, setCollapsed] = useState(true);
	const { t } = useTranslation();
	const mdDown = useMedia('mdDown');

	const options = useMemo(() => {
		if (!showSeeMore) return optionsProp;
		if (collapsed) return optionsProp.slice(0, maxItemsCollapsed);

		return optionsProp;
	}, [optionsProp, collapsed]);

	const isBlockedOption = planName => {
		return blockedOptions?.includes(planName);
	};

	useEffect(() => {
		if (!collapsed) {
			return;
		}
		if (
			optionsProp
				.slice(maxItemsCollapsed)
				.find(option => option.id === value)
		) {
			setCollapsed(false);
		}
	}, [optionsProp.length]);

	return (
		<Box sx={sx}>
			<RadioGroup
				aria-labelledby={ariaLabelledby}
				name={name}
				value={value || ''}
				sx={{ gap: '8px' }}
				{...props}
			>
				{options.map(
					({
						id,
						name: planName,
						label,
						price,
						rightLabelSuffix,
						description
					}) => {
						const selected = value?.toString?.() === id?.toString?.();

						const disabled = isBlockedOption(planName);

						return (
							<FormControlLabel
								key={id}
								value={id}
								control={<Radio />}
								disableTypography
								disabled={disabled}
								sx={({ palette }) => ({
									width: '100%',
									mx: 0,
									padding: {
										xs: '12px 12px 12px 8px',
										md: '12px 20px 12px 16px'
									},
									gap: { xs: '8px', md: '16px' },
									border: `1px solid ${selected ? palette.primary.main : palette.divider}`,
									borderRadius: '8px',
									minHeight: 92,
									alignItems: { xs: 'flex-start', md: 'center' },

									...(selected
										? {
												boxShadow:
													'0 2px 4px 0 rgba(161, 130, 243, 0.50)'
											}
										: {})
								})}
								label={
									<Stack
										direction={{ xs: 'column', md: 'row' }}
										alignItems={{ xs: 'flex-start', md: 'center' }}
										justifyContent="space-between"
										spacing={1}
										width={1}
									>
										<Stack gap="4px">
											<Typography
												variant={mdDown ? 'body1' : 'body2'}
												color={disabled ? 'grey.700' : 'text.black'}
												fontWeight={500}
											>
												{label}
											</Typography>

											{description && (
												<Typography
													variant="body0"
													color={
														disabled ? 'grey.700' : 'text.black'
													}
												>
													{description}
												</Typography>
											)}
										</Stack>
										<Stack
											sx={{
												display: 'flex',
												alignItems: 'center',
												justifyContent: 'center',
												gap: { xs: '8px', md: 0 },
												flexDirection: { xs: 'row', md: 'column' }
											}}
										>
											<Typography
												variant="body3"
												color={disabled ? 'grey.700' : 'text.black'}
												fontWeight="bold"
											>
												{price}
											</Typography>
											<Typography
												variant="body0"
												color={disabled ? 'grey.700' : 'text.black'}
											>
												{rightLabelSuffix}
											</Typography>
										</Stack>
									</Stack>
								}
							/>
						);
					}
				)}
				{optionsProp.length > maxItemsCollapsed && showSeeMore && (
					<Box
						sx={{
							display: 'flex',
							justifyContent: aligns[seeMoreAlign],
							mt: {
								xs: 0.5,
								md: 1.5
							}
						}}
					>
						<Button onClick={() => setCollapsed(!collapsed)}>
							{t(collapsed ? 'common.seeMore' : 'common.seeLess')}
						</Button>
					</Box>
				)}
			</RadioGroup>
		</Box>
	);
}
